import { useState } from 'react';

// ===== 業界相場比較データ =====
const marketComparison = [
  {
    item: 'COE・変更申請（書類作成のみ）',
    market: '100,000〜150,000円',
    ours: '65,000円〜',
    oursNote: '※プレミアム顧問価格',
    saving: '最大約57%OFF',
    halfNote: '相場の約半額！',
    icon: 'ri-file-text-line',
  },
  {
    item: '更新申請（申請取次込み）',
    market: '50,000〜80,000円',
    ours: '35,000円〜',
    oursNote: '※プレミアム顧問価格',
    saving: '最大約56%OFF',
    halfNote: '相場の約半額以下！',
    icon: 'ri-refresh-line',
  },
  {
    item: '建設業オプション',
    market: '100,000〜200,000円',
    ours: '60,000円',
    oursNote: '※全プラン共通',
    saving: '最大約70%OFF',
    halfNote: '相場の約半額以下！',
    icon: 'ri-building-2-line',
  },
];

// ===== 損益分岐点計算 =====
const calcSpotCoe = (n: number) => n === 0 ? 0 : 90000 + Math.max(0, n - 1) * 60000;
const calcStdCoe = (n: number) => n === 0 ? 0 : 20000 + 80000 + Math.max(0, n - 1) * 50000;
const calcPreCoe = (n: number) => n === 0 ? 0 : 40000 + 75000 + Math.max(0, n - 1) * 45000;

const calcSpotRenewal = (n: number) => n === 0 ? 0 : 40000 + Math.max(0, n - 1) * 30000;
const calcStdRenewal = (n: number) => n === 0 ? 0 : 20000 + 40000 + Math.max(0, n - 1) * 25000;
const calcPreRenewal = (n: number) => n === 0 ? 0 : 40000 + 35000 + Math.max(0, n - 1) * 25000;

const personRange = [1, 2, 3, 4, 5, 6];
const fmt = (n: number) => `¥${n.toLocaleString()}`;

// ===== 顧問プラン詳細 =====
const advisoryPlans = [
  {
    id: 'standard',
    label: 'スタンダード顧問プラン',
    monthlyFee: 20000,
    color: 'slate',
    features: [
      { icon: 'ri-file-list-3-line', text: '各種申請はスポット料金と同額' },
      { icon: 'ri-scissors-cut-line', text: '申請取次報酬が半額（¥20,000 → ¥10,000）' },
      { icon: 'ri-group-line', text: '【無料】3人以上の同時申請（同一事業所・同一職種）は申請取次報酬が無料！' },
      { icon: 'ri-chat-3-line', text: 'Chatwork相談：月5件まで無料' },
      { icon: 'ri-refresh-line', text: '不許可による再申請は初回申請価格の30%' },
    ],
  },
  {
    id: 'premium',
    label: 'プレミアム顧問プラン',
    monthlyFee: 40000,
    color: 'navy',
    badge: 'おすすめ',
    features: [
      { icon: 'ri-price-tag-3-line', text: '各種申請1人目が¥5,000引き' },
      { icon: 'ri-scissors-cut-line', text: '申請取次報酬が半額（¥20,000 → ¥10,000）' },
      { icon: 'ri-group-line', text: '【無料】3人以上の同時申請（同一事業所・同一職種）は申請取次報酬が無料！' },
      { icon: 'ri-chat-3-line', text: 'Chatwork相談：回数無制限' },
      { icon: 'ri-phone-line', text: '緊急時の電話対応（22時まで）' },
      { icon: 'ri-shield-check-line', text: '不許可による再申請が無料！' },
      { icon: 'ri-search-eye-line', text: '申請前の許可可能性事前相談' },
    ],
  },
];

const PricingSection = () => {
  const [breakEvenType, setBreakEvenType] = useState<'coe' | 'renewal'>('coe');

  const rows = personRange.map((n) => {
    const spot = breakEvenType === 'coe' ? calcSpotCoe(n) : calcSpotRenewal(n);
    const std = breakEvenType === 'coe' ? calcStdCoe(n) : calcStdRenewal(n);
    const pre = breakEvenType === 'coe' ? calcPreCoe(n) : calcPreRenewal(n);
    const stdSaving = spot - std;
    const preSaving = spot - pre;
    const best = pre <= std && pre <= spot ? 'premium' : std <= spot ? 'standard' : 'spot';
    return { n, spot, std, pre, stdSaving, preSaving, best };
  });

  const stdBreakEven = rows.find((r) => r.stdSaving >= 0)?.n ?? null;
  const preBreakEven = rows.find((r) => r.preSaving >= 0)?.n ?? null;

  return (
    <section id="pricing" className="py-16 md:py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ===== ヘッダー ===== */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-300 text-slate-700 text-xs md:text-sm font-bold px-4 md:px-5 py-2 rounded-full mb-4">
            <i className="ri-price-tag-3-fill text-slate-500"></i>
            事業者様向け 特別卸し価格
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">報酬額</h2>
          <p className="text-base md:text-lg text-slate-500">登録支援機関様・有料職業紹介会社様向けの特別卸し価格です</p>
        </div>

        {/* ===== 顧問契約 今すぐ得する訴求バナー ===== */}
        <div className="max-w-5xl mx-auto mb-10 md:mb-14">
          <div className="relative bg-gradient-to-br from-[#1a3a5c] via-[#1e4570] to-[#0f2540] rounded-3xl overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            <div className="relative px-5 md:px-8 py-8 md:py-10">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                {/* 左：メッセージ */}
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 bg-red-500 text-white text-xs font-extrabold px-3 md:px-4 py-1.5 rounded-full mb-4 tracking-wide">
                    <i className="ri-alarm-warning-fill"></i>
                    スポット料金のまま依頼し続けていませんか？
                  </div>
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-white leading-tight mb-3">
                    毎月の申請コストを<br />
                    <span className="text-amber-300 text-3xl md:text-4xl lg:text-5xl">大幅に削減</span>できます
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-5">
                    顧問契約なら月額費用を含めても、スポット料金より<strong className="text-white">圧倒的にお得</strong>。<br />
                    申請件数が多いほど、差は広がるばかりです。
                  </p>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#1a3a5c] px-6 md:px-7 py-3 rounded-full font-extrabold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i>
                    まず無料で相談してみる
                  </a>
                </div>
                {/* 右：節約額カード */}
                <div className="flex-shrink-0 grid grid-cols-2 gap-3 w-full md:w-auto">
                  <div className="bg-white/10 border border-white/20 rounded-2xl px-4 md:px-5 py-4 text-center">
                    <p className="text-white/60 text-[10px] font-bold mb-1 tracking-wider">月3人申請の場合</p>
                    <p className="text-amber-300 text-2xl md:text-3xl font-extrabold leading-none">¥10,000</p>
                    <p className="text-white/80 text-xs font-bold mt-1">スポット比お得</p>
                  </div>
                  <div className="bg-amber-400/20 border border-amber-400/40 rounded-2xl px-4 md:px-5 py-4 text-center">
                    <p className="text-amber-200 text-[10px] font-bold mb-1 tracking-wider">月5人申請の場合</p>
                    <p className="text-amber-300 text-2xl md:text-3xl font-extrabold leading-none">¥60,000</p>
                    <p className="text-white/80 text-xs font-bold mt-1">スポット比お得</p>
                  </div>
                  <div className="col-span-2 bg-red-500/30 border border-red-400/50 rounded-2xl px-4 md:px-5 py-4 text-center">
                    <p className="text-red-200 text-[10px] font-bold mb-1 tracking-wider">月5人×12ヶ月（年間）</p>
                    <p className="text-white text-3xl md:text-4xl font-extrabold leading-none">¥240,000</p>
                    <p className="text-red-200 text-sm font-extrabold mt-1">年間節約！</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== 業界相場との比較 ===== */}
        <div className="max-w-5xl mx-auto mb-12 md:mb-16">
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-slate-900 mb-2">
              一般的な行政書士事務所との<span className="text-[#1a3a5c]">報酬相場比較</span>
            </h3>
            <p className="text-slate-500 text-sm">弊所の最安値（プレミアム顧問価格）と業界相場を比較しています</p>
          </div>

          {/* 半額強調バナー */}
          <div className="bg-[#1a3a5c] rounded-2xl p-5 md:p-6 mb-6 text-white text-center">
            <p className="text-xs md:text-sm font-semibold opacity-80 mb-2 tracking-wide">
              <i className="ri-information-line mr-1"></i>
              一般的な行政書士事務所との報酬差をご存知ですか？
            </p>
            <p className="text-xl md:text-2xl lg:text-3xl font-extrabold">
              弊所の報酬は業界相場の<span className="text-amber-300 text-3xl md:text-4xl lg:text-5xl mx-2">約半額</span>以下！
            </p>
            <p className="text-white/70 text-xs mt-3">
              ※プレミアム顧問価格との比較。スポット料金でも業界相場より大幅に安い設定です。
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border-2 border-slate-200 mb-6">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="bg-[#1a3a5c]">
                  <th className="px-4 md:px-5 py-4 text-left text-xs md:text-sm font-bold text-white whitespace-nowrap">申請種別</th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs md:text-sm font-bold text-white whitespace-nowrap">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[10px] md:text-xs opacity-70">一般的な行政書士事務所</span>
                      <span>業界相場</span>
                    </div>
                  </th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs md:text-sm font-bold text-white whitespace-nowrap">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[10px] md:text-xs opacity-70">弊所の卸し価格（最安値）</span>
                      <span>弊所報酬</span>
                    </div>
                  </th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs md:text-sm font-bold text-white whitespace-nowrap">割引率</th>
                </tr>
              </thead>
              <tbody>
                {marketComparison.map((row, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                    <td className="px-4 md:px-5 py-4 md:py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
                          <i className={`${row.icon} text-slate-500 text-xs md:text-sm`}></i>
                        </div>
                        <span className="font-semibold text-slate-800 text-xs md:text-sm">{row.item}</span>
                      </div>
                    </td>
                    <td className="px-4 md:px-5 py-4 md:py-5 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-slate-400 font-bold line-through text-base md:text-xl">{row.market}</span>
                        <span className="text-xs text-slate-400">（税別）</span>
                      </div>
                    </td>
                    <td className="px-4 md:px-5 py-4 md:py-5 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-baseline gap-1">
                          <span className="text-[#1a3a5c] font-extrabold text-xl md:text-3xl">{row.ours}</span>
                          <span className="text-slate-400 text-xs">（税別）</span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-medium">{row.oursNote}</span>
                        <span className="inline-block bg-amber-100 text-amber-700 text-[11px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap mt-0.5">
                          {row.halfNote}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 md:px-5 py-4 md:py-5 text-center">
                      <span className="inline-block bg-[#1a3a5c] text-white text-xs md:text-sm font-extrabold px-3 md:px-4 py-1.5 md:py-2 rounded-full whitespace-nowrap">
                        {row.saving}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 強調コールアウト */}
          <div className="bg-[#1a3a5c] rounded-2xl p-5 md:p-6 text-white text-center">
            <p className="text-xs md:text-sm font-semibold mb-3 opacity-80 tracking-wide">
              <i className="ri-shield-check-line mr-2"></i>
              弊所の卸し価格（プレミアム顧問）
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 mb-4">
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/60 mb-1 tracking-wider">COE・変更申請</span>
                <span className="text-3xl md:text-5xl font-extrabold text-white">¥65,000<span className="text-xl md:text-2xl">〜</span></span>
              </div>
              <div className="w-px h-12 bg-white/20 hidden md:block"></div>
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/60 mb-1 tracking-wider">更新申請</span>
                <span className="text-3xl md:text-5xl font-extrabold text-white">¥35,000<span className="text-xl md:text-2xl">〜</span></span>
              </div>
            </div>
            <p className="text-white/80 text-xs md:text-sm">
              業界相場の<strong className="text-amber-300 text-sm md:text-base">半額以下</strong>で提供できるのは、弊所独自の効率化システムがあるから。<br className="hidden md:block" />
              ※スポット料金はCOE・変更申請¥70,000〜、更新申請¥40,000〜
            </p>
          </div>
        </div>

        {/* ===== 各プラン料金表 ===== */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-slate-900 mb-2">各プランの料金表</h3>
            <p className="text-slate-500 text-sm">安い順（プレミアム顧問 → スタンダード顧問 → スポット料金）で表示しています</p>
          </div>

          {/* ===== 顧問契約 共通案内バナー ===== */}
          <div className="mb-8 rounded-2xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
            <div className="bg-amber-400 px-5 md:px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white/40 rounded-lg flex-shrink-0">
                <i className="ri-gift-line text-white text-lg"></i>
              </div>
              <p className="text-white font-extrabold text-sm md:text-base tracking-wide">顧問契約プラン 共通特典</p>
            </div>
            <div className="px-5 md:px-6 py-5">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-5 mb-5">
                <div className="flex-shrink-0 bg-white border-2 border-amber-300 rounded-2xl px-6 md:px-8 py-4 text-center">
                  <p className="text-xs font-bold text-amber-600 mb-1 tracking-wider">顧問契約 共通</p>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#1a3a5c] leading-tight">初月顧問料</p>
                  <p className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-amber-500 leading-tight">無料！</p>
                  <p className="text-xs text-slate-500 mt-1">まずはお試しください</p>
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#1a3a5c] rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      <span className="font-bold text-slate-900">基本は1年契約</span>ですが、まず初月は顧問料が<span className="font-bold text-amber-600">完全無料</span>でお試しいただけます。
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#1a3a5c] rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      初月のお試し期間中に<span className="font-bold text-slate-900">継続しないと判断された場合</span>は、その時点で解約可能です。
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-information-line text-white text-xs"></i>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      ※初月お試し期間中に解約された場合、初月に顧問プラン料金で行った申請は<span className="font-bold text-slate-800">スポット料金が適用</span>となり、差額を請求させていただきます。
                    </p>
                  </div>
                </div>
              </div>
              {/* 特定技能プラン案内 */}
              <div className="mb-5 bg-red-50 border-2 border-red-400 rounded-xl px-4 md:px-5 py-4 flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-red-500 rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-star-fill text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-red-600 font-extrabold text-base md:text-lg lg:text-xl leading-tight">
                    特定技能「法務監修・申請取次」プラン <span className="text-xl md:text-2xl lg:text-3xl">3万円</span>のご利用可能
                  </p>
                  <p className="text-red-500 font-bold text-sm mt-1">※詳細はお問合せください</p>
                </div>
              </div>

              {/* ステップ表示 */}
              <div className="bg-white rounded-xl border border-amber-200 px-4 md:px-5 py-4">
                <p className="text-xs font-bold text-slate-500 mb-3 tracking-wider">顧問契約の流れ</p>
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-0">
                  {[
                    { step: '1', label: 'お問い合わせ', sub: '無料相談・プラン選択' },
                    { step: '2', label: '初月スタート', sub: '顧問料0円でお試し' },
                    { step: '3', label: '継続判断', sub: '1ヶ月後に継続 or 解約' },
                    { step: '4', label: '1年契約へ', sub: '継続の場合は年間契約' },
                  ].map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2 sm:gap-0 flex-1 w-full sm:w-auto">
                      <div className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-0 flex-1">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#1a3a5c] rounded-full text-white text-xs font-extrabold flex-shrink-0">
                          {s.step}
                        </div>
                        <div className="sm:text-center">
                          <p className="text-xs font-bold text-slate-800 sm:mt-1 whitespace-nowrap">{s.label}</p>
                          <p className="text-[10px] text-slate-500 whitespace-nowrap">{s.sub}</p>
                        </div>
                      </div>
                      {idx < 3 && (
                        <div className="hidden sm:flex items-center justify-center w-6 flex-shrink-0 sm:mb-4">
                          <i className="ri-arrow-right-s-line text-slate-300 text-xl"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ===== プレミアム顧問 料金表 ===== */}
          <div className="mb-10">
            <div className="bg-[#1a3a5c] rounded-t-2xl px-5 md:px-6 pt-5 pb-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 bg-amber-400 text-white text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-trophy-fill"></i> 最もお得
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-fire-fill text-amber-300"></i> 一番人気
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-star-fill text-amber-300"></i> おすすめ
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                    プレミアム顧問プラン
                  </h4>
                  <p className="text-white/70 text-xs mt-0.5">月額 <span className="text-white font-bold text-sm">¥40,000</span>（税別）・初月無料でお試し可能</p>
                </div>
                <div className="flex-shrink-0 bg-white/10 border border-white/20 rounded-xl px-4 md:px-5 py-3 text-center">
                  <p className="text-amber-300 text-lg md:text-2xl font-extrabold leading-tight">申請1人目</p>
                  <p className="text-amber-300 text-xl md:text-3xl font-extrabold leading-none">5,000円引き</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { icon: 'ri-scissors-cut-line', text: '申請取次が半額', highlight: false },
                  { icon: 'ri-group-line', text: '3人以上の取次報酬が無料！', highlight: true },
                  { icon: 'ri-shield-check-line', text: '不許可再申請無料', highlight: false },
                  { icon: 'ri-phone-line', text: '22時まで電話対応', highlight: false },
                ].map((pt, idx) => (
                  <div key={idx} className={`flex items-center gap-2 rounded-lg px-2 md:px-3 py-2 ${pt.highlight ? 'bg-red-500 border-2 border-red-300' : 'bg-white/10'}`}>
                    <i className={`${pt.icon} text-xs md:text-sm flex-shrink-0 ${pt.highlight ? 'text-white' : 'text-amber-300'}`}></i>
                    <span className={`text-[10px] md:text-xs font-extrabold ${pt.highlight ? 'text-white' : 'text-white'}`}>{pt.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-amber-400/20 border border-amber-400/40 rounded-xl px-4 md:px-5 py-3 flex flex-col md:flex-row items-center gap-3 md:gap-8">
                <div className="flex items-center gap-2">
                  <i className="ri-money-cny-circle-fill text-amber-300 text-2xl flex-shrink-0"></i>
                  <span className="text-white font-extrabold text-sm">月5人申請なら年間</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-amber-300 text-3xl md:text-4xl font-extrabold">¥240,000</span>
                  <span className="text-white/80 text-sm font-bold">もスポット比でお得！</span>
                </div>
                <div className="md:ml-auto text-white/60 text-xs">※COE・変更申請（申請取次込み）の場合</div>
              </div>
            </div>
            <div className="overflow-x-auto border-x-2 border-[#1a3a5c]/30 border-b-0">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-[#1a3a5c]/10">
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-[#1a3a5c] whitespace-nowrap">申請種別</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-[#1a3a5c] whitespace-nowrap">1人目</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-[#1a3a5c] whitespace-nowrap">2人目以降<br /><span className="font-normal text-[#1a3a5c]/70 text-[10px]">同一事業所・同一職種・同時申請</span></th>
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-[#1a3a5c] whitespace-nowrap">備考</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-bold text-slate-800 whitespace-nowrap text-xs md:text-sm">COE・変更申請（書類作成のみ）</td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥65,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥35,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap"><span className="text-amber-600 font-bold">スポット比 ¥5,000引き</span></td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/40">
                    <td className="px-4 md:px-5 py-3 font-bold text-slate-800 whitespace-nowrap text-xs md:text-sm">更新申請（申請取次料金込み）</td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥35,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥25,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap"><span className="text-amber-600 font-bold">スポット比 ¥5,000引き</span></td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-bold text-slate-800 whitespace-nowrap text-xs md:text-sm">入管申請取次（オプション選択）</td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥10,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-center whitespace-nowrap">
                      <span className="font-extrabold text-[#1a3a5c] text-lg md:text-xl">¥10,000</span>
                    </td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap"><span className="text-amber-600 font-bold">スポット比 半額（¥20,000→¥10,000）</span></td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/40">
                    <td className="px-4 md:px-5 py-3 font-bold text-slate-800 whitespace-nowrap text-xs md:text-sm">建設業オプション</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">全プラン共通</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-b-2xl border-x-2 border-b-2 border-[#1a3a5c]/30 bg-white px-5 md:px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 flex items-center justify-center bg-[#1a3a5c] rounded flex-shrink-0">
                  <i className="ri-list-check text-white text-xs"></i>
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">プレミアム顧問プランの内容</p>
                {advisoryPlans[1].badge && (
                  <span className="bg-amber-400 text-white text-[10px] font-extrabold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    {advisoryPlans[1].badge}
                  </span>
                )}
              </div>
              <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2.5">
                {advisoryPlans[1].features.map((feat, fIdx) => {
                  const isFree = feat.text.startsWith('【無料】');
                  const displayText = isFree ? feat.text.replace('【無料】', '') : feat.text;
                  return (
                    <li key={fIdx} className={`flex items-start gap-2.5 ${isFree ? 'md:col-span-2 bg-red-50 border-2 border-red-400 rounded-xl px-4 py-3' : ''}`}>
                      <div className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 mt-0.5 ${isFree ? 'bg-red-500' : 'bg-[#1a3a5c]/10'}`}>
                        <i className={`${feat.icon} text-xs ${isFree ? 'text-white' : 'text-[#1a3a5c]'}`}></i>
                      </div>
                      <div>
                        {isFree && (
                          <span className="inline-block bg-red-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full mr-2 mb-0.5 whitespace-nowrap">無料！</span>
                        )}
                        <span className={`text-sm leading-relaxed ${isFree ? 'text-red-700 font-extrabold text-base' : 'text-slate-700'}`}>{displayText}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-[#1a3a5c] hover:bg-[#0f2540] text-white px-5 md:px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  プレミアム顧問プランで問い合わせる
                </a>
              </div>
            </div>
          </div>

          {/* ===== スタンダード顧問 料金表 ===== */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-slate-600 text-white text-xs font-extrabold px-4 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-medal-line"></i> 2番目にお得
              </span>
              <h4 className="text-base md:text-lg font-extrabold text-slate-900">スタンダード顧問プラン</h4>
              <span className="text-sm text-slate-500 font-medium">月額¥20,000（税別）</span>
            </div>
            <div className="overflow-x-auto rounded-t-2xl border-2 border-slate-300 border-b-0">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-slate-700 whitespace-nowrap">申請種別</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-slate-700 whitespace-nowrap">1人目</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-slate-700 whitespace-nowrap">2人目以降<br /><span className="font-normal text-slate-500 text-[10px]">同一事業所・同一職種・同時申請</span></th>
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-slate-700 whitespace-nowrap">備考</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">COE・変更申請（書類作成のみ）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥70,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥40,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">スポットと同額</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/40">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">更新申請（申請取次料金込み）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥40,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥25,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">2人目以降はスポット比 ¥5,000引き</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">入管申請取次（オプション選択）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥10,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥10,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">スポット比 半額（¥20,000→¥10,000）</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/40">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">建設業オプション</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">全プラン共通</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-b-2xl border-2 border-slate-300 border-t border-t-slate-100 bg-white px-5 md:px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 flex items-center justify-center bg-slate-600 rounded flex-shrink-0">
                  <i className="ri-list-check text-white text-xs"></i>
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">スタンダード顧問プランの内容</p>
              </div>
              <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2.5">
                {advisoryPlans[0].features.map((feat, fIdx) => {
                  const isFree = feat.text.startsWith('【無料】');
                  const displayText = isFree ? feat.text.replace('【無料】', '') : feat.text;
                  return (
                    <li key={fIdx} className={`flex items-start gap-2.5 ${isFree ? 'md:col-span-2 bg-red-50 border-2 border-red-400 rounded-xl px-4 py-3' : ''}`}>
                      <div className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 mt-0.5 ${isFree ? 'bg-red-500' : 'bg-slate-100'}`}>
                        <i className={`${feat.icon} text-xs ${isFree ? 'text-white' : 'text-slate-600'}`}></i>
                      </div>
                      <div>
                        {isFree && (
                          <span className="inline-block bg-red-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full mr-2 mb-0.5 whitespace-nowrap">無料！</span>
                        )}
                        <span className={`text-sm leading-relaxed ${isFree ? 'text-red-700 font-extrabold text-base' : 'text-slate-700'}`}>{displayText}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white px-5 md:px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  スタンダード顧問プランで問い合わせる
                </a>
              </div>
            </div>
          </div>

          {/* ===== スポット料金 ===== */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-slate-400 text-white text-xs font-extrabold px-4 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-price-tag-2-line"></i> スポット
              </span>
              <h4 className="text-base md:text-lg font-extrabold text-slate-900">スポット料金</h4>
              <span className="text-sm text-slate-500 font-medium">月額費用なし・単発依頼</span>
            </div>
            <div className="overflow-x-auto rounded-2xl border-2 border-slate-200">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-slate-600 whitespace-nowrap">申請種別</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-slate-600 whitespace-nowrap">1人目</th>
                    <th className="px-4 md:px-5 py-3 text-center text-xs font-bold text-slate-600 whitespace-nowrap">2人目以降<br /><span className="font-normal text-slate-400 text-[10px]">同一事業所・同一職種・同時申請</span></th>
                    <th className="px-4 md:px-5 py-3 text-left text-xs font-bold text-slate-600 whitespace-nowrap">備考</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">COE・変更申請（書類作成のみ）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥70,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥40,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">—</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/50">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">更新申請（申請取次料金込み）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥40,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥30,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">—</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">入管申請取次（オプション選択）</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥20,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-extrabold text-slate-700 text-sm md:text-base whitespace-nowrap">¥20,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">—</td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-slate-50/50">
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-800 whitespace-nowrap text-xs md:text-sm">建設業オプション</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-center font-semibold text-slate-700 whitespace-nowrap">¥60,000</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 消費税・注意書き */}
        <div className="max-w-6xl mx-auto mb-12 md:mb-16">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 md:px-6 py-4 text-sm text-slate-600 space-y-1.5">
            <p className="flex items-start gap-2">
              <i className="ri-information-line text-slate-400 mt-0.5 flex-shrink-0"></i>
              <span><span className="font-bold text-slate-700">上記報酬額はすべて税別です。別途消費税が加算されます。</span></span>
            </p>
            <p className="flex items-start gap-2">
              <i className="ri-information-line text-slate-400 mt-0.5 flex-shrink-0"></i>
              申請に係る手数料などの実費は弊所報酬と別途必要（スポット・顧問契約プランとも共通）
            </p>
            <p className="flex items-start gap-2">
              <i className="ri-information-line text-slate-400 mt-0.5 flex-shrink-0"></i>
              受入企業様から直接ご依頼の場合は一般価格となります。詳細はお問合せ時にご説明いたします。
            </p>
            <p className="flex items-start gap-2">
              <i className="ri-alert-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span><span className="font-bold text-red-600">申請者の状況など申請難易度が高い場合は、別途加算報酬が発生する場合があります。</span>詳細は事前にご相談ください。</span>
            </p>
          </div>
        </div>

        {/* ===== 料金プラン一覧（特典比較） ===== */}
        <div className="max-w-6xl mx-auto mb-12 md:mb-16">
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-slate-900 mb-2">料金プラン一覧</h3>
            <p className="text-slate-500 text-sm">各プランのサービス内容を一覧で比較できます</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr>
                  <th className="px-4 md:px-5 py-4 text-left text-xs font-bold text-slate-600 bg-slate-50 whitespace-nowrap border-b border-slate-200">サービス内容</th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs font-bold text-slate-600 bg-slate-50 whitespace-nowrap border-b border-slate-200">
                    スポット料金<br /><span className="font-normal text-slate-400">月額なし</span>
                  </th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs font-bold text-slate-700 bg-slate-100 whitespace-nowrap border-b border-slate-300">
                    スタンダード顧問<br /><span className="font-normal text-slate-500">月額¥20,000</span>
                  </th>
                  <th className="px-4 md:px-5 py-4 text-center text-xs font-bold text-[#1a3a5c] bg-[#1a3a5c]/8 whitespace-nowrap border-b border-[#1a3a5c]/20">
                    プレミアム顧問<br /><span className="font-normal text-[#1a3a5c]/70">月額¥40,000</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'COE・変更申請（1人目）', spot: '¥70,000', std: '¥70,000', pre: '¥65,000', preHighlight: true },
                  { label: 'COE・変更申請（2人目以降）', spot: '¥40,000', std: '¥40,000', pre: '¥35,000', preHighlight: true },
                  { label: '更新申請（1人目）', spot: '¥40,000', std: '¥40,000', pre: '¥35,000', preHighlight: true },
                  { label: '更新申請（2人目以降）', spot: '¥30,000', std: '¥25,000', pre: '¥25,000', stdHighlight: true, preHighlight: true },
                  { label: '入管申請取次（オプション選択）', spot: '¥20,000', std: '¥10,000（半額！）', pre: '¥10,000（半額！）', stdHighlight: true, preHighlight: true, stdSub: '🎉 3人以上の同時申請（同一事業所・同一職種）は申請取次報酬が無料！', preSub: '🎉 3人以上の同時申請（同一事業所・同一職種）は申請取次報酬が無料！' },
                  { label: 'Chatwork相談', spot: '—', std: '月5件まで無料', pre: '回数無制限', stdHighlight: true, preHighlight: true },
                  { label: '緊急電話対応', spot: '—', std: '—', pre: '22時まで対応', preHighlight: true },
                  { label: '不許可時の再申請', spot: '通常料金', std: '初回の30%', pre: '無料！', stdHighlight: true, preHighlight: true },
                  { label: '申請前の許可可能性相談', spot: '—', std: '—', pre: 'あり', preHighlight: true },
                  { label: '初月顧問料', spot: '—', std: '無料（お試し）', pre: '無料（お試し）', stdHighlight: true, preHighlight: true },
                ].map((row, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                    <td className="px-4 md:px-5 py-3 font-semibold text-slate-700 text-xs whitespace-nowrap">{row.label}</td>
                    <td className="px-4 md:px-5 py-3 text-center text-xs text-slate-500 whitespace-nowrap">{row.spot}</td>
                    <td className={`px-4 md:px-5 py-3 text-center text-xs ${row.stdHighlight ? 'text-slate-700 font-bold' : 'text-slate-500'}`}>
                      <div className="whitespace-nowrap">{row.std}</div>
                      {row.stdSub && (
                        <div className="text-red-600 font-bold text-[10px] mt-1 leading-tight">{row.stdSub}</div>
                      )}
                    </td>
                    <td className={`px-4 md:px-5 py-3 text-center text-xs ${row.preHighlight ? 'text-[#1a3a5c] font-bold' : 'text-slate-500'}`}>
                      <div className="whitespace-nowrap">{row.pre}</div>
                      {row.preSub && (
                        <div className="text-red-600 font-bold text-[10px] mt-1 leading-tight">{row.preSub}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===== 顧問契約の損益分岐点 ===== */}
        <div className="max-w-5xl mx-auto mb-12 md:mb-16">
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-300 text-slate-700 text-xs md:text-sm font-bold px-4 md:px-5 py-2 rounded-full mb-4">
              <i className="ri-bar-chart-2-line text-lg"></i>
              顧問契約の月額費用はいつペイできる？
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-slate-900 mb-2">
              何人申請すれば顧問契約がお得になるか
            </h3>
            <p className="text-slate-500 text-sm">
              顧問月額費用を含めた合計コストで比較。<br className="hidden md:block" />
              スポット料金より安くなる行を<span className="text-[#1a3a5c] font-bold">ハイライト</span>しています。
            </p>
          </div>

          {/* タブ切り替え */}
          <div className="flex justify-center mb-6">
            <div className="flex flex-col sm:flex-row bg-slate-100 rounded-2xl p-1 w-full sm:w-auto gap-1 sm:gap-0">
              <button
                onClick={() => setBreakEvenType('coe')}
                className={`flex-1 sm:flex-none px-3 md:px-5 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all duration-200 cursor-pointer text-center ${
                  breakEvenType === 'coe'
                    ? 'bg-white text-[#1a3a5c] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                COE・変更申請（申請取次込み）
              </button>
              <button
                onClick={() => setBreakEvenType('renewal')}
                className={`flex-1 sm:flex-none px-3 md:px-5 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all duration-200 cursor-pointer text-center ${
                  breakEvenType === 'renewal'
                    ? 'bg-white text-[#1a3a5c] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                更新申請（申請取次込み）
              </button>
            </div>
          </div>

          {/* 前提条件の注記 */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 md:px-5 py-3 mb-4 text-xs text-slate-500 max-w-3xl mx-auto">
            {breakEvenType === 'coe' ? (
              <p>
                ※スポット：1人目¥90,000（書類作成¥70,000＋取次¥20,000）、2人目以降¥60,000（¥40,000＋¥20,000）<br />
                ※スタンダード：月額¥20,000＋1人目¥80,000（¥70,000＋取次¥10,000）、2人目以降¥50,000（¥40,000＋¥10,000）<br />
                ※プレミアム：月額¥40,000＋1人目¥75,000（¥65,000＋取次¥10,000）、2人目以降¥45,000（¥35,000＋¥10,000）
              </p>
            ) : (
              <p>
                ※スポット：1人目¥40,000、2人目以降¥30,000（申請取次込み）<br />
                ※スタンダード：月額¥20,000＋1人目¥40,000、2人目以降¥25,000（申請取次込み）<br />
                ※プレミアム：月額¥40,000＋1人目¥35,000、2人目以降¥25,000（申請取次込み）
              </p>
            )}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 mb-6">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-bold text-slate-600 whitespace-nowrap">月間申請人数</th>
                  <th className="px-3 md:px-4 py-3 text-center text-xs font-bold text-slate-600 whitespace-nowrap">スポット</th>
                  <th className="px-3 md:px-4 py-3 text-center text-xs font-bold text-slate-700 whitespace-nowrap">スタンダード顧問<br /><span className="font-normal text-slate-400">（月額¥20,000込み）</span></th>
                  <th className="px-3 md:px-4 py-3 text-center text-xs font-bold text-[#1a3a5c] whitespace-nowrap">プレミアム顧問<br /><span className="font-normal text-slate-400">（月額¥40,000込み）</span></th>
                  <th className="px-3 md:px-4 py-3 text-center text-xs font-bold text-slate-600 whitespace-nowrap">判定</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const stdCheaper = row.stdSaving > 0;
                  const stdEven = row.stdSaving === 0;
                  const preCheaper = row.preSaving > 0;
                  const isBestRow = row.best === 'premium' && preCheaper;
                  return (
                    <tr key={i} className={`border-t border-slate-100 ${isBestRow ? 'bg-amber-50' : preCheaper || stdCheaper ? 'bg-[#1a3a5c]/5' : ''}`}>
                      <td className={`px-3 md:px-4 py-3 font-bold whitespace-nowrap ${isBestRow ? 'text-amber-700' : 'text-slate-800'}`}>
                        {row.n}人
                        {isBestRow && <span className="ml-1 inline-flex items-center gap-0.5 bg-amber-400 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full whitespace-nowrap"><i className="ri-trophy-fill"></i>最安</span>}
                      </td>
                      <td className="px-3 md:px-4 py-3 text-center font-semibold text-slate-700 whitespace-nowrap text-xs md:text-sm">{fmt(row.spot)}</td>
                      <td className={`px-3 md:px-4 py-3 text-center font-semibold whitespace-nowrap text-xs md:text-sm ${
                        stdCheaper ? 'text-slate-700 font-bold' : stdEven ? 'text-slate-600' : 'text-slate-400'
                      }`}>
                        {fmt(row.std)}
                        {stdCheaper && (
                          <div className="text-xs text-red-600 font-extrabold">▼¥{row.stdSaving.toLocaleString()}お得</div>
                        )}
                        {stdEven && <div className="text-[10px] text-slate-400">同額</div>}
                      </td>
                      <td className={`px-3 md:px-4 py-3 text-center font-semibold whitespace-nowrap text-xs md:text-sm ${
                        preCheaper ? 'text-[#1a3a5c] font-extrabold' : 'text-slate-400'
                      }`}>
                        <div className={isBestRow ? 'text-[#1a3a5c] text-base md:text-lg font-extrabold' : ''}>{fmt(row.pre)}</div>
                        {preCheaper && (
                          <div className={`font-extrabold ${isBestRow ? 'text-sm md:text-base text-red-600' : 'text-xs text-red-600'}`}>▼¥{row.preSaving.toLocaleString()}お得</div>
                        )}
                      </td>
                      <td className="px-3 md:px-4 py-3 text-center whitespace-nowrap">
                        {row.best === 'premium' ? (
                          <span className="inline-flex items-center gap-1 bg-[#1a3a5c] text-white text-[10px] font-bold px-2 py-1 rounded-full">
                            <i className="ri-trophy-line"></i> プレミアム最安
                          </span>
                        ) : row.best === 'standard' ? (
                          <span className="inline-flex items-center gap-1 bg-slate-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                            <i className="ri-medal-line"></i> スタンダード最安
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-slate-200 text-slate-600 text-[10px] font-bold px-2 py-1 rounded-full">
                            スポット最安
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 損益分岐点まとめ */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 md:p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-slate-600 rounded-lg flex-shrink-0">
                  <i className="ri-medal-line text-white text-base"></i>
                </div>
                <span className="font-bold text-slate-800 text-sm">スタンダード顧問（月額¥20,000）</span>
              </div>
              {stdBreakEven !== null ? (
                <p className="text-slate-700 text-sm leading-relaxed">
                  月<span className="font-extrabold text-xl text-slate-700 mx-1">{stdBreakEven}人</span>以上の申請があれば、顧問月額費用がペイできてスポットより<strong>お得</strong>になります。
                </p>
              ) : (
                <p className="text-slate-700 text-sm">6人以上の申請でお得になります。</p>
              )}
            </div>
            <div className="bg-[#1a3a5c]/5 border border-[#1a3a5c]/20 rounded-2xl p-4 md:p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-[#1a3a5c] rounded-lg flex-shrink-0">
                  <i className="ri-trophy-line text-white text-base"></i>
                </div>
                <span className="font-bold text-[#1a3a5c] text-sm">プレミアム顧問（月額¥40,000）</span>
              </div>
              {preBreakEven !== null ? (
                <p className="text-[#1a3a5c]/80 text-sm leading-relaxed">
                  月<span className="font-extrabold text-xl text-[#1a3a5c] mx-1">{preBreakEven}人</span>以上の申請があれば、顧問月額費用がペイできてスポットより<strong>お得</strong>になります。さらに不許可時の再申請無料など付加価値も最大です。
                </p>
              ) : (
                <p className="text-[#1a3a5c]/80 text-sm">6人以上の申請でお得になります。</p>
              )}
            </div>
          </div>

          {/* ===== 今すぐ始めるべき理由 CTA ===== */}
          <div className="bg-gradient-to-r from-[#1a3a5c] to-[#0f2540] rounded-3xl p-6 md:p-8 text-center">
            <div className="inline-flex items-center gap-2 bg-red-500 text-white text-xs font-extrabold px-3 md:px-4 py-1.5 rounded-full mb-4 tracking-wide">
              <i className="ri-time-line"></i>
              初月顧問料 完全無料でお試し可能
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-white mb-3">
              リスクゼロで始められます
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-xl mx-auto">
              初月は顧問料が<strong className="text-amber-300">完全無料</strong>。合わなければ解約OK。<br />
              まずは無料でお試しいただき、その効果を実感してください。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-6">
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 md:px-5 py-3 w-full sm:w-auto justify-center">
                <i className="ri-check-double-line text-amber-300 text-lg flex-shrink-0"></i>
                <span className="text-white text-sm font-bold whitespace-nowrap">初月顧問料 無料</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 md:px-5 py-3 w-full sm:w-auto justify-center">
                <i className="ri-check-double-line text-amber-300 text-lg flex-shrink-0"></i>
                <span className="text-white text-sm font-bold whitespace-nowrap">合わなければ解約可能</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 md:px-5 py-3 w-full sm:w-auto justify-center">
                <i className="ri-check-double-line text-amber-300 text-lg flex-shrink-0"></i>
                <span className="text-white text-sm font-bold whitespace-nowrap">相談・見積もり無料</span>
              </div>
            </div>
            <a
              href="#contact"
              className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-[#1a3a5c] px-8 md:px-10 py-4 rounded-full font-extrabold text-sm md:text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line text-lg"></i>
              今すぐ無料で相談・お問い合わせ
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <p className="text-white/40 text-xs mt-3">お気軽にどうぞ。返信は通常1営業日以内です。</p>
          </div>
        </div>

        {/* ===== 安い理由 ===== */}
        <div className="max-w-5xl mx-auto mb-10 md:mb-12">
          <div className="bg-[#1a3a5c] rounded-2xl p-6 md:p-8 text-white">
            <h3 className="text-lg md:text-xl font-extrabold text-center mb-5 md:mb-6 flex items-center justify-center gap-2">
              <i className="ri-question-line text-amber-400 text-2xl"></i>
              業界トップクラスの安い卸し価格で提供できる理由
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 md:gap-5">
              <div className="bg-white/10 rounded-xl p-4 md:p-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-3">
                  <i className="ri-file-list-2-line text-amber-300 text-xl"></i>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  弊所より申請に必要な書類リストをお渡しし、登録支援機関様・有料職業紹介会社様に収集いただくことで、弊所の作業時間を大幅に短縮できます
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 md:p-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-3">
                  <i className="ri-send-plane-line text-amber-300 text-xl"></i>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  お客様は弊所がお渡しする必要書類リストに沿って書類を収集し、弊所へ送付いただくだけで申請が可能。シンプルな流れで効率化を実現
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 md:p-5">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-3">
                  <i className="ri-team-line text-amber-300 text-xl"></i>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  行政書士2名＋事務員1名の3名体制で大量案件を効率処理。1人事務所ではできないマンパワーで対応しているため、低価格を実現
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div className="text-center">
          <p className="text-slate-700 font-medium mb-4 text-sm md:text-base">
            料金の詳細・ご不明な点はお気軽にお問い合わせください
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-[#1a3a5c] hover:bg-[#0f2540] text-white px-8 md:px-10 py-4 rounded-full font-bold text-sm md:text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            無料でお問い合わせする
          </a>
        </div>

      </div>
    </section>
  );
};

export default PricingSection;
